/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/conferencesApi.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    API functions for Conferences module - all API calls centralized here.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { ConferenceRoom } from '../types';

export interface ListParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  domainId?: number;
}

export interface ListResponse {
  data: ConferenceRoom[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Fetches list of conferences with pagination and filters
 */
export async function fetchConferences(params: ListParams): Promise<ListResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  });

  if (params.search?.trim()) {
    queryParams.append('search', params.search.trim());
  }

  // Add domain filter if provided
  if (params.domainId) {
    queryParams.append('domainId', params.domainId.toString());
  }

  const response = await fetch(
    buildApiUrl(`/api/telephony/conferences?${queryParams}`),
    {
      method: 'GET',
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch conferences');
  }

  const result = await response.json();
  return {
    data: result.data || [],
    pagination: result.pagination || {
      page: params.page,
      limit: params.limit,
      total: 0,
      totalPages: 0,
    },
  };
}

/**
 * Fetches a single conference by ID
 */
export async function fetchConferenceById(id: string | number): Promise<ConferenceRoom> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/conferences/${id}`),
    {
      method: 'GET',
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch conference');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Creates a new conference
 */
export async function createConference(data: Partial<ConferenceRoom>): Promise<ConferenceRoom> {
  const response = await fetch(
    buildApiUrl('/api/telephony/conferences'),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to create conference' }));
    throw new Error(errorData.message || 'Failed to create conference');
  }

  const result = await response.json();

  // Handle different response formats
  // Some APIs return { data: {...} }, others return the data directly
  const savedData = result.data || result;

  if (!savedData) {
    throw new Error('Create operation did not return data');
  }

  return savedData;
}

/**
 * Updates an existing conference
 */
export async function updateConference(id: string | number, data: Partial<ConferenceRoom>): Promise<ConferenceRoom> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/conferences/${id}`),
    {
      method: 'PUT',
      headers: getApiHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to update conference' }));
    throw new Error(errorData.message || 'Failed to update conference');
  }

  const result = await response.json();

  // Handle different response formats
  // Some APIs return { data: {...} }, others return the data directly
  const savedData = result.data || result;

  if (!savedData) {
    // If API doesn't return data, return the data we sent with the ID
    return { ...data, id: String(id) } as ConferenceRoom;
  }

  return savedData;
}

/**
 * Deletes a conference by ID
 */
export async function deleteConference(id: string | number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/conferences/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    let message = 'Failed to delete conference';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(message);
  }
}

/**
 * Fetches conference XML for a given conference ID
 */
export async function fetchConferenceXml(id: string | number): Promise<string> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/conferences/${id}/xml`),
    {
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return await response.text();
}

/**
 * Fetches quicklist of conferences
 */
export async function fetchConferenceQuicklist(params?: {
  search?: string;
  limit?: number;
  tenantSlug?: string;
  userId?: string | number;
}): Promise<ConferenceRoom[]> {
  const queryParams = new URLSearchParams();

  if (params?.search) {
    queryParams.append('search', params.search);
  }
  if (params?.limit) {
    queryParams.append('limit', params.limit.toString());
  }

  const url = buildApiUrl(`/api/telephony/conferences/quicklist${queryParams.toString() ? `?${queryParams}` : ''}`);
  const response = await fetch(url, {
    headers: getApiHeaders(params?.tenantSlug, params?.userId)
  });

  if (!response.ok) {
    throw new Error('Failed to fetch conference quicklist');
  }

  const result = await response.json();
  return result.data || [];
}

